import store from './store.js';
import { v4 as uuidv4 } from 'uuid';
import findIndex from 'lodash/findIndex';
import * as Sentry from '@sentry/browser';

import ContentEncryptionService from './components/lib/ContentEncryptionService.js';
import Decrypter from './components/lib/Decrypter.js';
import Encrypter from './components/lib/Encrypter.js';
import { flashcardsAPI, toEpubR2Location } from './communication/flashcards';
import { interactionEventsTracker } from './communication/analytics';
import { truncateWithEllipsis } from '../../_shared_/js/utils/stringUtils';
import { assignReadingOrder, compareFlashcardsByReadingPosition } from './flashcardReadingOrder.js';

/**
 * Maximum characters accepted by the host for a flashcard's front text.
 * Mirrors the `frontText` limit declared in @publicala/delfino.
 */
export const FLASHCARD_FRONT_TEXT_MAX_LENGTH = 500;

/**
 * Maximum characters accepted by the host for a flashcard's back text.
 * Mirrors the `backText` limit declared in @publicala/delfino.
 */
export const FLASHCARD_BACK_TEXT_MAX_LENGTH = 1000;

export default {
    id: null,
    uuid: null,
    color: '',
    colorSlug: null,
    frontText: '',
    backText: '',
    sourceText: null,
    sourceTextLength: null,
    aiGenerated: false,

    // PDF specific
    startPage: null,
    startPageStartOffset: null,
    startPageLength: null,
    endPage: null,
    endPageLength: null,

    // @R2 EPUB R2 specific - CSS selector-based positioning
    epubChapter: '',
    epubR2Location: null,
    location: null,

    // @R2 Highlight render contract — lets HighlightManager paint the flashcard marker
    // reusing the same pipeline as highlights. `isRendered`/`contextBodyDomNode` are set
    // by HighlightManager during rendering; `totalText`/`totalLength` are render-aliases of
    // `sourceText`/`sourceTextLength` (kept in sync via syncHighlightRenderAliases).
    isRendered: false,
    contextBodyDomNode: null,
    totalText: '',
    totalLength: null,

    create() {
        this.uuid = uuidv4();
    },

    isStored() {
        return Object.prototype.hasOwnProperty.call(this, 'id') && this.id != null && this.id !== '';
    },

    /**
     * Always false: flashcards are R2-only (CSS selector-based) and never use the legacy
     * R1 offset format. Mirrors highlightModel.isLegacy so HighlightManager branches identically.
     * @returns {boolean}
     */
    isLegacy() {
        return false;
    },

    /**
     * Discriminator so shared render code (HighlightManager) can route a flashcard marker
     * click to the flashcard handler instead of the highlight selection tool.
     * @returns {boolean}
     */
    isFlashcard() {
        return true;
    },

    /**
     * CSS class that uniquely identifies this flashcard's marker in the DOM.
     * Mirrors highlightModel.getUniqueCssClass.
     * @returns {string}
     */
    getUniqueCssClass() {
        return `uuid-${this.uuid}`;
    },

    /**
     * CSS classes applied to the rendered marker. Mirrors highlightModel.getCssClassForMarker,
     * plus `marker-underline` so HighlightManager paints flashcards as an underline instead of
     * a background fill (a style-level flag, not a type check).
     * @returns {string}
     */
    getCssClassForMarker() {
        return `${this.getUniqueCssClass()} ${this.isStored() ? 'confirmed' : 'unconfirmed'} marker-underline`;
    },

    /**
     * Maps flashcard-native fields onto the highlight render contract so HighlightManager can
     * paint the marker without knowing about flashcards: sourceText→totalText,
     * sourceTextLength→totalLength.
     * @returns {void}
     */
    syncHighlightRenderAliases() {
        this.totalText = this.sourceText;
        this.totalLength = this.sourceTextLength;
    },

    /**
     * Copies the positioning + render-context fields from a selection onto this flashcard so it
     * paints through the shared HighlightManager pipeline (the highlight render contract). Guards
     * by data shape, not platform: PDF selections carry page offsets; EPUB R2 selections carry a
     * CSS-selector `location`, the chapter href and the live body node. Centralizing it here (next
     * to the render-contract aliases) keeps the UI from hand-copying — and silently dropping — a
     * field (e.g. contextBodyDomNode, whose omission left EPUB underlines unrendered).
     * @param {Object} selection - The source selection/highlight (store `currentHighlight`)
     * @returns {Object} This flashcard instance, for chaining
     */
    applySelectionPosition(selection) {
        if (!selection) return this;

        if (selection.startPage != null) {
            this.startPage = selection.startPage;
            this.startPageStartOffset = selection.startPageStartOffset;
            this.startPageLength = selection.startPageLength;
            this.endPage = selection.endPage;
            this.endPageLength = selection.endPageLength;
        }

        if (selection.location?.selection) {
            this.location = selection.location;
            this.epubChapter = selection.epubChapter;
            this.contextBodyDomNode = selection.contextBodyDomNode;
        }

        return this;
    },

    /**
     * @R2 Reconstructs the R2 location from the stored epubR2Location so the EPUB
     * underline renders via CSS selectors (mirrors highlightModel.reHydrate). The host
     * index endpoint returns a flat camelCase shape (epubR2Location, startPage, epubChapter
     * at top level), already copied onto the instance, so the reading-order util reads those
     * directly. Also decrypts the user-content text fields when the session uses encryption.
     * @returns {Promise<Object>} The rehydrated flashcard instance
     */
    async reHydrate() {
        const r2Location = this.epubR2Location;

        if (r2Location?.startCss) {
            this.location = { selection: r2Location };
        } else if (r2Location?.text) {
            this.location = { selection: { text: r2Location.text } };
        }

        [this.frontText, this.backText, this.sourceText] = await Promise.all([
            this.maybeDecryptText(this.frontText),
            this.maybeDecryptText(this.backText),
            this.sourceText ? this.maybeDecryptText(this.sourceText) : this.sourceText,
        ]);

        this.syncHighlightRenderAliases();

        return this;
    },

    /**
     * Truncates the editable text fields to the maximum length the host accepts,
     * so persisting never exceeds the model limits. Mutates in place (before
     * encryption) so the stored instance matches what is sent to the host.
     * @returns {void}
     */
    truncateTextFieldsToMaxLength() {
        this.frontText = truncateWithEllipsis(this.frontText, FLASHCARD_FRONT_TEXT_MAX_LENGTH);
        this.backText = truncateWithEllipsis(this.backText, FLASHCARD_BACK_TEXT_MAX_LENGTH);
    },

    /**
     * Persists a new flashcard and appends it to the store. The text fields are
     * encrypted for the wire while the instance keeps the plaintext for display.
     * After appending, best-effort persists the global reading-position order via
     * persistReadingOrder.
     * @returns {Promise<boolean>} True if persisted, false on failure
     */
    async store() {
        try {
            this.truncateTextFieldsToMaxLength();
            const [frontText, backText, sourceText] = await Promise.all([
                this.maybeEncryptText(this.frontText),
                this.maybeEncryptText(this.backText),
                this.sourceText ? this.maybeEncryptText(this.sourceText) : this.sourceText,
            ]);

            const response = await flashcardsAPI.store({ ...this, frontText, backText, sourceText });

            this.id = response.id;
            // Snapshot the flat shape the host lists back, not the in-memory wrapper, so "go to
            // source" and the reading order read the same card before and after a reload. A card
            // without an EPUB selection (PDF, or created from the panel) has none.
            this.epubR2Location = toEpubR2Location(this.location) ?? null;
            this.syncHighlightRenderAliases();

            store.state.flashcards.push(this);
            interactionEventsTracker.track('flashcard', 'create', this.id);
            await this.persistReadingOrder();
            return true;
        } catch (e) {
            console.error('flashcard store failed', e);
            Sentry.captureException(e, {
                tags: { feature: 'flashcards' },
                extra: { method: 'store' },
            });
            return false;
        }
    },

    /**
     * Updates the editable fields (front/back/color) of a stored flashcard and
     * reflects the change in the store. source_text is immutable after create.
     * After reflecting the change, best-effort persists the global reading-position
     * order via persistReadingOrder.
     * @returns {Promise<boolean>} True if the store was updated, false on failure or missing id
     */
    async update() {
        try {
            this.truncateTextFieldsToMaxLength();
            const [frontText, backText] = await Promise.all([
                this.maybeEncryptText(this.frontText),
                this.maybeEncryptText(this.backText),
            ]);

            await flashcardsAPI.update(this.id, {
                frontText,
                backText,
                color: this.color,
                colorSlug: this.colorSlug,
            });

            const index = findIndex(store.state.flashcards, ['id', this.id]);
            if (index === -1) return false;
            store.state.flashcards.splice(index, 1, { ...store.state.flashcards[index], ...this });
            await this.persistReadingOrder();
            return true;
        } catch (e) {
            console.error('flashcard update failed', e);
            Sentry.captureException(e, {
                tags: { feature: 'flashcards' },
                extra: { method: 'update', flashcardId: this.id },
            });
            return false;
        }
    },

    /**
     * Recomputes the global reading-position order over ALL flashcards and persists only the
     * {uuid, sortOrder} entries of the user's OWN cards that changed via flashcardsAPI.reorder;
     * then applies the new order to those in-memory instances and re-sorts store.state.flashcards
     * so the panel reflects it. No network call when nothing changed (idempotent). Foreign/subscribed
     * cards (colorSlug present in store.state.sharedColors) participate in the ordering — they keep
     * their reading slot, so an owned card that follows a shared one is assigned the higher
     * sortOrder, and the slot stays reserved until the shared card is deleted — but they are never
     * sent in the payload, since the host reorder is owner-scoped. Best-effort: a failure is logged
     * and reported to Sentry (warning level) but does not fail the create/update that triggered it.
     * @returns {Promise<void>}
     */
    async persistReadingOrder() {
        try {
            const spine = store.state.epub?.spine || [];
            const sharedSlugs = new Set((store.state.sharedColors || []).map(color => color.slug));
            const isOwnedFlashcard = flashcard => !sharedSlugs.has(flashcard.colorSlug);

            const { changed } = assignReadingOrder(store.state.flashcards, spine, isOwnedFlashcard);

            if (changed.length > 0) {
                await flashcardsAPI.reorder({ flashcards: changed });
                const newSortOrderByUuid = new Map(changed.map(entry => [entry.uuid, entry.sortOrder]));
                store.state.flashcards.forEach(flashcard => {
                    if (newSortOrderByUuid.has(flashcard.uuid)) {
                        flashcard.sortOrder = newSortOrderByUuid.get(flashcard.uuid);
                    }
                });
            }

            store.state.flashcards.sort((a, b) => compareFlashcardsByReadingPosition(a, b, spine));
        } catch (e) {
            console.error('flashcard reorder failed', e);
            Sentry.captureException(e, {
                level: 'warning',
                tags: { feature: 'flashcards' },
                extra: { method: 'persistReadingOrder' },
            });
        }
    },

    /**
     * Deletes a stored flashcard and removes it from the store.
     * @returns {Promise<boolean>} True if the flashcard was removed, false on failure or missing id
     */
    async destroy() {
        try {
            await flashcardsAPI.delete(this.id);

            const index = findIndex(store.state.flashcards, ['id', this.id]);
            if (index === -1) return false;
            store.state.flashcards.splice(index, 1);
            return true;
        } catch (e) {
            console.error('flashcard destroy failed', e);
            Sentry.captureException(e, {
                tags: { feature: 'flashcards' },
                extra: { method: 'destroy', flashcardId: this.id },
            });
            return false;
        }
    },

    /**
     * Decrypts a text field received from the host when the session uses
     * client-side encryption.
     * @param {string|null} text - The text to decrypt
     * @returns {Promise<string|null>} The decrypted text, or the input unchanged
     */
    async maybeDecryptText(text) {
        if (ContentEncryptionService.shouldDecrypt() && text != null) {
            return await Decrypter.decryptText(text);
        }

        return text;
    },

    /**
     * Encrypts a text field for the wire when the session uses client-side
     * encryption. The host decrypts it before persisting.
     * @param {string|null} text - The text to encrypt
     * @returns {Promise<string|null>} The encrypted text, or the input unchanged
     */
    async maybeEncryptText(text) {
        if (ContentEncryptionService.shouldEncrypt() && text != null) {
            return await Encrypter.encryptText(text);
        }

        return text;
    },
};
